import {Cost, CostType} from './cost';
import {Employee} from './employee';
import {Configuration, EmiConfiguration} from '../services/configuration/configuration';
import {BenefitPlan} from './benefit-plan';
import {Benefit, RequireNominee} from './benefit';
import {Nominee} from './nominee';
import {CartChange,
  CartChangeType,
  CART_SUMMARY_TOTAL_TITLE,
  CartSummaryTableRowViewModel,
  CartSummaryTotalKey,
  CartSummaryTableTotalsRowViewModel,
  CompleteCart,
  CompletedCartAddedByType,
  WalletConsumption} from '../services/cart/models';
import {Dependent, DependentRelationships} from './dependent';
import {BenefitSection} from './benefit-section';
import {PolicyGroupPolicyType} from './policy-group';
import {BenefitsDisplayable, BenefitsDisplayableType} from './benefit-displayable';
import {Constants} from 'src/app/shared/constants/constants';
import {WALLET_CONSTANTS,
  WalletHistory} from 'src/app/px/enrolment/policy-group-overview/employee-overview/wallet-history/models/wallet';
import {WalletEventType} from 'src/app/px/enrolment/policy-group-overview/employee-overview/wallet-history/models/wallet.enum';
import {KeyValue} from '@angular/common';

export enum ScreenType {
  BENEFITS_SELECTION = 0,
  BENEFIT_SUMMARY = 1
}

export class Cart {
  private nomineesForBenefitPlan: Map<number, Nominee[]> = new Map<
    number,
    Nominee[]
  >();
  private nomineesUrlForBenefitPlan: Map<number, string> = new Map<
    number,
    string
  >();
  private dependentsForBenefitPlan: Map<number, number[]> = new Map<
    number,
    number[]
  >();
  private dependentsByEnrolment: Map<number, Set<number>> = new Map<
    number,
    Set<number>
  >();

  private parentSectionIdBySection = new Map<number, number | null>();

  private planAddedBy = new Map<number, number>();

  constructor(
    public sections: CartSection[],
    public includeTax: boolean,
    public wallet: number,
    public fixedCost: number,
    public hasCompletedCart: boolean,
    public selectedEmi?: EmiConfiguration,
    public createdAt?: string,
    public endorsementId?: number,
    public midTerm?: boolean,
    public consumedWallet?: number,
    public includeSeparateEmployerContribution: boolean = false
  ) {}

  public static make(
    configuration: Configuration,
    benefitSections: BenefitSection[],
    benefits: Benefit[],
    walletAmount: number,
    fixedCostAmount: number,
    completedCart: CompleteCart | undefined
  ): Cart {
    const benefitById = new Map<number, Benefit>();

    benefits.forEach((benefit) => benefitById.set(benefit.id, benefit));
    const cartSections = benefitSections
      .filter((section) => section.parentSectionId == null)
      .sort((a, b) => a.ordinal - b.ordinal)
      .map(
        (section) => new CartSection(
          section.id,
          section.title,
          new Map<number, BenefitPlan[]>(),
          benefitById,
          completedCart?.endorsementId
        )
      );

    return new Cart(
      [
        ...cartSections,
        new CartSection(
          null,
          cartSections.length === 0 ? 'Benefits' : 'Other Benefits',
          new Map<number, BenefitPlan[]>(),
          benefitById,
          completedCart?.endorsementId
        ),
      ],
      configuration?.portal.tax,
      walletAmount,
      fixedCostAmount,
      completedCart?.id !== undefined,
      configuration?.emi.find((emi) => emi.id === completedCart?.emiId),
      completedCart?.createdAt,
      completedCart?.endorsementId,
      completedCart?.midTerm,
      completedCart?.consumedWallet,
      configuration?.portal.separateEmployerContribution ?? false
    );
  }

  removalPlans(
    previousDependent: Dependent,
    newDependent: Dependent
  ): BenefitPlan[] {
    const removalPlans: BenefitPlan[] = [];

    let relationIsDifferent = true;

    if (previousDependent.relationship === newDependent.relationship) {
      relationIsDifferent = false;
    } else {
      if (
        DependentRelationships.SPOUSE_RELATIONSHIPS.includes(
          previousDependent.relationship
        ) &&
        DependentRelationships.SPOUSE_RELATIONSHIPS.includes(
          newDependent.relationship
        )
      ) {
        relationIsDifferent = false;
      }

      if (
        DependentRelationships.CHILD_RELATIONSHIPS.includes(
          previousDependent.relationship
        ) &&
        DependentRelationships.CHILD_RELATIONSHIPS.includes(
          newDependent.relationship
        )
      ) {
        relationIsDifferent = false;
      }

      if (
        DependentRelationships.PARENT_RELATIONSHIPS.includes(
          previousDependent.relationship
        ) &&
        DependentRelationships.PARENT_RELATIONSHIPS.includes(
          newDependent.relationship
        )
      ) {
        relationIsDifferent = false;
      }

      if (
        DependentRelationships.PARENT_IN_LAW_RELATIONSHIPS.includes(
          previousDependent.relationship
        ) &&
        DependentRelationships.PARENT_IN_LAW_RELATIONSHIPS.includes(
          newDependent.relationship
        )
      ) {
        relationIsDifferent = false;
      }

      if (
        DependentRelationships.SIBLING_RELATIONSHIPS.includes(
          previousDependent.relationship
        ) &&
        DependentRelationships.SIBLING_RELATIONSHIPS.includes(
          newDependent.relationship
        )
      ) {
        relationIsDifferent = false;
      }

      if (
        DependentRelationships.GRANDPARENT_RELATIONSHIPS.includes(
          previousDependent.relationship
        ) &&
        DependentRelationships.GRANDPARENT_RELATIONSHIPS.includes(
          newDependent.relationship
        )
      ) {
        relationIsDifferent = false;
      }

      if (
        DependentRelationships.UNCLE_OR_AUNT_RELATIONSHIPS.includes(
          previousDependent.relationship
        ) &&
        DependentRelationships.UNCLE_OR_AUNT_RELATIONSHIPS.includes(
          newDependent.relationship
        )
      ) {
        relationIsDifferent = false;
      }
    }
    this.sections.forEach((section) => {
      section.benefitToBenefitOptionMap.forEach((plans) => {
        plans.forEach((plan) => {
          if (
            this.dependentsForBenefitPlan
              .get(plan.id)
              ?.find((v) => v === previousDependent.id)
          ) {
            removalPlans.push(plan);
          }
        });
      });
    });

    if (relationIsDifferent) {
      return removalPlans;
    }

    if (previousDependent.dateOfBirth !== newDependent.dateOfBirth) {
      return removalPlans.filter((plan) => plan.cost.ageRangeRater.length > 0);
    }

    return [];
  }

  get benefitPlanDependents() {
    return this.dependentsForBenefitPlan;
  }

  get endorsementDependents() {
    return this.dependentsByEnrolment;
  }

  nomineeForBenefitPlan(plan: BenefitPlan): Nominee[] {
    return this.nomineesForBenefitPlan.get(plan.id) || [];
  }

  nomineeUrlForBenefitPlan(planId: number): string | undefined {
    return this.nomineesUrlForBenefitPlan.get(planId);
  }

  // Retrieve all dependents associated with plans
  // If the plan is added to the cart, get all the dependents saved for the plan
  // Otherwise, get all eligible dependents for the plan
  dependentsForPlan(plan: BenefitPlan, currentEmployee: Employee): Dependent[] {
    if (!currentEmployee) {
      return [];
    }
    if (this.dependentsForBenefitPlan.has(plan.id)) {
      const selectedDependents = currentEmployee.dependents.filter((d) => {
        return this.dependentsForBenefitPlan.get(plan.id)!.includes(d.id);
      });

      plan.selectedDependents = selectedDependents;

      return selectedDependents;
    } else {
      return plan
        .dependentsForPlans(currentEmployee);
    }
  }

  addedPlansForBenefit(benefit: Benefit): BenefitPlan[] {
    const allPlansAddedToCart: BenefitPlan[] = [];

    benefit.allPlans.forEach((plan) => {
      if (this.benefitPlanIsAddedToCart(benefit, plan)) {
        allPlansAddedToCart.push(plan);
      }
    });

    return allPlansAddedToCart;
  }

  benefitIsAddedToCart(benefit: Benefit): boolean {
    return this.benefitMapForBenefit(benefit).has(benefit.id);
  }

  benefitPlanIsAddedToCart(
    benefit: Benefit,
    benefitPlan: BenefitPlan
  ): boolean {
    const benefitMap: Map<number, BenefitPlan[]> =
      this.benefitMapForBenefit(benefit);

    return (
      benefitMap.has(benefit.id) &&
      benefitMap.get(benefit.id)?.find((plan) => plan.id === benefitPlan.id) !==
        undefined
    );
  }

  isUserAddedPlan(benefitId: number): boolean {
    return this.planAddedBy.get(benefitId) === CompletedCartAddedByType.SELF;
  }

  isAdminAddedPlan(benefitId: number): boolean {
    return this.planAddedBy.get(benefitId) === CompletedCartAddedByType.ADMIN;
  }

  public benefitMapForBenefit(benefit: Benefit): Map<number, BenefitPlan[]> {
    if (this.sections.length === 0) {
      return new Map<number, BenefitPlan[]>();
    }
    let currentParentId = benefit.parentSectionId;
    let rootParentId = currentParentId;

    while (currentParentId != null) {
      rootParentId = currentParentId;
      currentParentId =
        this.parentSectionIdBySection.get(currentParentId) || null;
    }

    let cartSection = this.sections.find(
      (section) => section.benefitSectionId === rootParentId
    );

    if (cartSection === undefined) {
      cartSection = this.sections[this.sections.length - 1];
    }

    return cartSection.benefitToBenefitOptionMap;
  }

  canApplyChangeIfCompleted(change: CartChange) {
    if (!this.hasCompletedCart) {
      return true;
    }

    switch (change.change) {
        case CartChangeType.ADDITION:
        case CartChangeType.REMOVAL:
          if (this.createdAt && change.changeAt && this.createdAt >= change.changeAt) {
            return true;
          }
          break;
        case CartChangeType.ADD_NOMINEES:
        case CartChangeType.UPLOAD_NOMINEE_FORM:
        case CartChangeType.VISIT_BENEFITS_PAGE:
        case CartChangeType.VISIT_USER_PROFILE_PAGE:
        case CartChangeType.ADD_MIDTERM_DEPENDENT:
        case CartChangeType.START_MIDTERM_ENROLMENT:
        case CartChangeType.END_MIDTERM_ENROLMENT:
        case CartChangeType.RESET_DEFAULT:
          return true;
    }

    return false;
  }

  applyChange(change: CartChange, benefits: Benefit[]) {
    const benefit = benefits.find((benefit) => benefit.id === change.benefitId);
    const plan = benefit?.allPlans.find((plan) => plan.id === change.planId);

    if (!(benefit !== undefined && plan !== undefined)) {
      return;
    }

    const benefitMap = this.benefitMapForBenefit(benefit);
    const benefitPlans: BenefitPlan[] = benefitMap.get(benefit.id) || [];
    const add = (plans: BenefitPlan[], map: Map<number, BenefitPlan[]>) => {
      // To check if plan is already in added benefitToBenefitOptionMap against a benefit
      const isPlanAdded = map
        .get(benefit.id)
        ?.find((plan) => plan.id === change.planId);

      if (!isPlanAdded) {
        plans.push(plan);
      }
      map.set(benefit.id, plans);
    };
    const remove = (plans: BenefitPlan[], map: Map<number, BenefitPlan[]>) => {
      const matchingPlanIndex = plans.findIndex(
        (benefitPlan) => benefitPlan.id === change.planId
      );

      // There is an invalid deletion. Ignore it.
      if (matchingPlanIndex === -1) {
        return;
      }

      plans.splice(matchingPlanIndex, 1);

      // If we deleted the last plan, remove the benefit entry from the cart too.
      if (plans.length === 0) {
        map.delete(benefit.id);
      }
    };

    switch (change.change) {
        case CartChangeType.ADDITION:
        // First apply the change to the active cart.
          add(benefitPlans, benefitMap);
          this.planAddedBy.set(benefit.id, change.addedBy!);
          this.dependentsForBenefitPlan.set(plan.id, change.dependentsIds || []);
          break;
        case CartChangeType.REMOVAL:
        // First apply the change to the active cart.
          remove(benefitPlans, benefitMap);

          this.planAddedBy.delete(benefit.id);
          this.nomineesForBenefitPlan.delete(change.planId);
          this.nomineesUrlForBenefitPlan.delete(change.planId);
          this.dependentsForBenefitPlan.delete(plan.id);
          break;
        case CartChangeType.ADD_NOMINEES:
          this.nomineesForBenefitPlan.set(change.planId, change.nominees || []);
          break;
        case CartChangeType.UPLOAD_NOMINEE_FORM:
          this.nomineesUrlForBenefitPlan.set(
            change.planId,
            change.uploadNomineesForm || ''
          );
    }
  }

  // Grab the dependents from previous endorsements
  previousEndorsementDependents(change: CartChange) {
    const endorsementId = change.endorsementId;

    if (!endorsementId) {
      return;
    }

    if (!this.dependentsByEnrolment.has(endorsementId)) {
      this.dependentsByEnrolment.set(
        endorsementId,
        new Set<number>([])
      );
    }

    switch (change.change) {
        case CartChangeType.ADDITION:
          change.dependentsIds?.forEach((dependent) => {
            this.dependentsByEnrolment
              .get(endorsementId)
              ?.add(dependent);
          });
          break;
        case CartChangeType.REMOVAL:
          change.dependentsIds?.forEach((dependent) => {
            this.dependentsByEnrolment
              .get(endorsementId)
              ?.delete(dependent);
          });
    }
  }

  get nonEmptySections(): CartSection[] {
    return this.sections.filter((section) => section.hasAddedPlans);
  }

  totalAmount(
    currentEmployee: Employee,
    midTermCalculations: boolean,
    useGrossCost: boolean = false
  ): Cost {
    let sum = new Cost(
      Cost.NO_COST_ID,
      CostType.FIXED,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      []
    );

    this.sections.forEach((section) => {
      sum = sum.adding(
        section.totalForSection(
          currentEmployee,
          this.benefitPlanDependents,
          midTermCalculations,
          useGrossCost
        ),
        currentEmployee
      );
    });

    return sum;
  }

  totalEmployerContribution(
    currentEmployee: Employee,
    midTermCalculations: boolean
  ): Cost {
    return this
      .totalAmount(currentEmployee, midTermCalculations, true)
      .subtractingValue(
        this.totalAmount(currentEmployee, midTermCalculations, false)
      );
  }

  totalPremiumAmount(
    currentEmployee: Employee,
    midTermCalculations: boolean
  ): Cost {
    let sum = this.totalAmount(currentEmployee, midTermCalculations)
      .subtractingValue(this.walletPoints())
      .subtractingValue(new Cost(
        -1,
        CostType.FIXED,
        this.fixedCostExcessContributionAmount(currentEmployee),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        []
      ))
      .addingValue(this.employeeFixedCost(midTermCalculations));

    if (this.includeTax) {
      sum = sum.adding(
        this.totalGstAmount(currentEmployee, midTermCalculations),
        currentEmployee
      );
    }

    return sum;
  }

  describeDueAmounts(
    employee: Employee
  ) {
    // EMI should be calculated on the total premium cost, not the total premium amount with fixed cost.
    // This is because EMI is only applicable to the premium cost and not the fixed cost.
    const totalCost = this.totalPremiumAmount(employee, false);
    const total = `Total Cost ${totalCost.displayValue(employee)}`;

    if (this.selectedEmi) {
      const emiAmount = totalCost.dividing(this.selectedEmi?.numberOfMonth || 1, employee).displayValue(employee);

      return `${emiAmount} x ${this.selectedEmi?.numberOfMonth} months | ${total}`;
    }

    return total;
  }

  fixedCostExcessContributionAmount(
    currentEmployee: Employee
  ): number {
    let excessContribution = 0;

    this.sections.forEach((section) => {
      section.benefitToBenefitOptionMap.forEach((plans, benefitId) => {
        const benefit = section.benefitsById.get(benefitId);

        if (!benefit) {
          return;
        }

        plans.forEach((plan) => {
          if (plan.cost.type !== CostType.FIXED || plan.costCovered.type !== CostType.FIXED) {
            return;
          }

          const selectedDependents = this.dependentsForPlan(plan, currentEmployee);
          const employeeCost = plan.getPremiumValueForPlan(
            benefit.mtPremiumCalculation,
            currentEmployee,
            benefit.policyType,
            selectedDependents
          );

          if (employeeCost.value < 0) {
            excessContribution += Math.abs(employeeCost.value);
          }
        });
      });
    });

    return excessContribution;
  }

  totalGstAmount(
    currentEmployee: Employee,
    midTermCalculations: boolean
  ): Cost {
    let total = this.totalAmount(currentEmployee, midTermCalculations);

    if (this.wallet) {
      total = total.subtractingValue(this.walletPoints());
    }

    return total.dividing(100 / currentEmployee.tax, currentEmployee);
  }

  walletPoints(): Cost {
    return new Cost(
      -1,
      CostType.FIXED,
      this.wallet,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      []
    );
  }

  employeeFixedCost(midTermCalculations: boolean): Cost {
    return new Cost(
      -1,
      CostType.FIXED,
      midTermCalculations ? 0 : this.fixedCost,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      []
    );
  }

  totalPremiumAmountWithFixedCost(
    currentEmployee: Employee,
    midTermCalculations: boolean,
    walletVisibility: boolean,
    walletAvailableToConsume: Cost,
    additionalWalletCredit?: Cost
  ): Cost {
    const walletConsumed = walletVisibility ?
      this.walletToBeConsumed(currentEmployee, false, walletAvailableToConsume) :
      this.walletPoints();

    let sum = this.totalAmount(currentEmployee, midTermCalculations)
      .subtractingValue(walletConsumed)
      .addingValue(this.employeeFixedCost(midTermCalculations));

    if (additionalWalletCredit) {
      sum = sum.subtractingValue(additionalWalletCredit);
    }

    if (this.includeTax) {
      sum = sum.adding(
        this.totalGstAmount(currentEmployee, midTermCalculations),
        currentEmployee
      );
    }

    sum = sum.adding(
      this.planFixedCost(currentEmployee, midTermCalculations),
      currentEmployee
    );

    return sum;
  }

  balanceWallet(currentEmployee: Employee,
    midTermCalculations: boolean): Cost {
    let balanceWallet = 0;

    if (currentEmployee && currentEmployee.salary) {
      balanceWallet =
        this.wallet - this.totalAmount(currentEmployee, midTermCalculations).value > 0 ?
          this.wallet - this.totalAmount(currentEmployee, midTermCalculations).value :
          0;
    }

    return new Cost(
      -1,
      CostType.FIXED,
      balanceWallet,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      []
    );
  }

  walletToBeConsumed(
    currentEmployee: Employee,
    midTermCalculations: boolean,
    walletAvailableToConsume: Cost
  ): Cost {
    // Pick the smaller of available wallet and total plan cost, and never allow negative wallet consumption
    const walletToConsume = Math.max(0, Math.min(
      walletAvailableToConsume.value,
      this.totalAmount(currentEmployee, midTermCalculations).value
    ));

    return new Cost(
      -1,
      CostType.FIXED,
      walletToConsume,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      []
    );
  }

  planFixedCost(employee: Employee, midTermCalculations: boolean): Cost {
    if (midTermCalculations) {
      return new Cost(-1, CostType.FIXED, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, []);
    }

    let total = new Cost(
      Cost.NO_COST_ID,
      CostType.FIXED,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      []
    );

    this.sections.forEach((section) => {
      section.benefitToBenefitOptionMap.forEach((plans) => {
        plans.forEach((plan) => {
          total = total.addingValue(
            plan.fixedCost.calculateProRata(
              employee,
              plan.policyStartDate,
              plan.policyEndDate
            )
          );
        });
      });
    });

    return total;
  }

  // To check if all mandatory benefit plan is added in cart
  isDefaultPlanAddedToCart(defaultPlanMap: Map<number, BenefitPlan>, userBenefits: Benefit[]) {
    for (const key of defaultPlanMap.keys()) {
      if (!this.benefitIsAddedToCart(userBenefits.find((ben) => ben.id === key)!)) {
        return false;
      }
    }

    return true;
  }

  // Generate a new cart summary view model
  cartRowsViewModal(
    displayable: BenefitsDisplayable,
    employee: Employee,
    subDisplayable?: BenefitsDisplayable[]
  ) : CartSummaryTableRowViewModel {
    switch (displayable.type) {
        case BenefitsDisplayableType.SECTION:
          return {
            id: `section-${displayable.id}`,
            title: displayable.section!.title,
            description: '',
            premium: this.premiumForSection(displayable.section!.id, employee)
              ?.displayValue(employee, 0) || '',
            type: displayable.type,
            whosCovered: '',
            sumInsured: '',
            canDelete: false,
            subViewModels: subDisplayable!
              .filter((d) => d.isBenefit)
              .map((d) => {
                return this.benefitViewModel(d.benefit!, employee);
              }),
            dontAllowChangesAfterCheckout: false,
          };
        case BenefitsDisplayableType.BENEFIT:
          return this.benefitViewModel(displayable.benefit!, employee);
    }
  }

  cartTotalViewModels(
    configuration: Configuration,
    employee: Employee,
    showEmi: boolean,
    walletConsumption: WalletConsumption,
    screenType: ScreenType,
    isWalletUnlockedForMarketplace: boolean,
    isShowingWalletGlobally: boolean,
    additionalWalletCredit: number = 0
  ): CartSummaryTableTotalsRowViewModel[] {
    const isBenefitSummaryScreen = screenType === ScreenType.BENEFIT_SUMMARY;
    const positiveAdditionalWalletCredit = Math.max(0, additionalWalletCredit);

    const positvieAdditionalWalletCreditCost = new Cost(
      -1,
      CostType.FIXED,
      positiveAdditionalWalletCredit,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      []
    );

    const totalViewModels: CartSummaryTableTotalsRowViewModel[] = [
      {
        key: CartSummaryTotalKey.TOTAL_PLAN_COST,
        title: CART_SUMMARY_TOTAL_TITLE.TOTAL_PLAN_COST,
        value: this
          .totalAmount(employee, false)
          .displayValue(employee),
        style: 'regular',
      },
    ];

    if (this.walletPoints().value > 0) {
      totalViewModels.push({
        title: 'Company Contribution',
        value: this.walletPoints()
          .displayValue(employee),
        style: 'regular',
      });
    }

    if (!isWalletUnlockedForMarketplace && (this.walletPoints().value > 0 || positiveAdditionalWalletCredit > 0)) {
      const totalAmountValue = this.totalAmount(employee, false).value;
      const availableWallet = walletConsumption.walletAvailableToConsume.value + positiveAdditionalWalletCredit;
      const walletLeftToUtilize = new Cost(
        -1,
        CostType.FIXED,
        Math.max(0, availableWallet - totalAmountValue),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        []
      );

      totalViewModels.push({
        title: 'Available Wallet',
        value: walletLeftToUtilize.displayValue(employee),
        style: 'regular',
      });
    }

    const isOtherDeductionPresent =
      (walletConsumption?.otherDeduction?.marketplace?.value ?? 0) > 0 ||
      (walletConsumption?.otherDeduction?.claims?.value ?? 0) > 0;

    const showLastEnrolmentConsumption = isWalletUnlockedForMarketplace &&
      (isShowingWalletGlobally || isOtherDeductionPresent) &&
      this.walletPoints().value > 0 &&
      walletConsumption.lastEnrolmentConsumption.value > 0 &&
      !isBenefitSummaryScreen;

    if (showLastEnrolmentConsumption) {
      totalViewModels.push({
        title: 'Wallet utilised for earlier plan selection',
        value: walletConsumption.lastEnrolmentConsumption.displayValue(employee),
        style: 'regular',
        additionalText: WALLET_CONSTANTS.WALLET_UTILISED,
      });
    }

    if (isWalletUnlockedForMarketplace &&
      this.walletPoints().value > 0 &&
      isOtherDeductionPresent) {
      const deductionLabels: string[] = [];

      if ((walletConsumption?.otherDeduction?.marketplace?.value ?? 0) > 0) {
        deductionLabels.push(`Marketplace Deduction : ${walletConsumption.otherDeduction?.marketplace?.displayValue(employee)}`);
      }

      if ((walletConsumption?.otherDeduction?.claims?.value ?? 0) > 0) {
        deductionLabels.push(`${configuration.text.cart.vitalPlus} Claims Deduction : ${walletConsumption.otherDeduction?.claims?.displayValue(employee)}`);
      }
      totalViewModels.push({
        title: 'Other Deduction',
        value: walletConsumption.marketplaceConsumption.displayValue(employee),
        style: 'regular',
        additionalText: deductionLabels.join(', '),
      });
    }

    if (isWalletUnlockedForMarketplace &&
      isShowingWalletGlobally &&
      (this.walletPoints().value > 0 || positiveAdditionalWalletCredit > 0)) {
      const totalAmountValue = this.totalAmount(employee, false).value;
      const availableWallet = walletConsumption.walletAvailableToConsume.value + positiveAdditionalWalletCredit;
      const walletLeftToUtilize = new Cost(
        -1,
        CostType.FIXED,
        Math.max(0, availableWallet - totalAmountValue),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        []
      ).displayValue(employee);

      totalViewModels.push({
        title: 'Available wallet',
        value: walletLeftToUtilize,
        style: 'regular',
        additionalText: showLastEnrolmentConsumption ? WALLET_CONSTANTS.AVAILABLE_WALLET : undefined,
      });
    }

    if (configuration.portal.tax) {
      totalViewModels.push({
        title: `GST Amount`,
        value: this
          .totalGstAmount(employee, false)
          .displayValue(employee),
        style: 'regular',
      });
    }

    if (this.planFixedCost(employee, false)
      .value > 0) {
      totalViewModels.push({
        title: 'Fixed Contribution',
        value: this.planFixedCost(employee, false)
          .displayValue(employee),
        style: 'regular',
      });
    }

    if (configuration.portal.separateEmployerContribution) {
      totalViewModels.push({
        key: CartSummaryTotalKey.EMPLOYER_CONTRIBUTION,
        title: CART_SUMMARY_TOTAL_TITLE.EMPLOYER_CONTRIBUTION,
        value: this
          .totalEmployerContribution(employee, false)
          .displayValue(employee),
        style: 'regular',
      });
    }

    const totalPremiumWithFixedCost = this.totalPremiumAmountWithFixedCost(employee,
      false,
      isWalletUnlockedForMarketplace,
      walletConsumption.walletAvailableToConsume,
      positvieAdditionalWalletCreditCost);

    totalViewModels.push({
      key: CartSummaryTotalKey.YOUR_COST,
      title: CART_SUMMARY_TOTAL_TITLE.YOUR_COST,
      value: totalPremiumWithFixedCost.displayValue(employee),
      style: 'informational',
    });

    if (configuration.userDetails.includeEmi && this.selectedEmi && showEmi) {
      totalViewModels.push({
        key: CartSummaryTotalKey.EMI,
        title: 'EMI',
        value: this.describeDueAmounts(employee),
        style: 'success',
      });
    }

    return totalViewModels;
  }

  premiumForSection(sectionId: number, employee : Employee): Cost | undefined {
    return this.sections.find((section) => section.benefitSectionId === sectionId)
      ?.totalForSection(
        employee,
        this.benefitPlanDependents,
        false,
        this.includeSeparateEmployerContribution
      );
  }

  benefitViewModel(
    benefit: Benefit,
    employee: Employee
  ): CartSummaryTableRowViewModel {
    const plans = this.addedPlansForBenefit(benefit);
    const selectedDependentsForPlan = this.dependentsForPlan(plans[0], employee);

    // 1. Flatten Premium Calculation
    const premium = plans.reduce((totalPremium, plan, index) => {
      const planPremium = plan.getPremiumValueForPlan(
        benefit.mtPremiumCalculation,
        employee,
        benefit.policyType,
        this.dependentsForPlan(plans[index], employee),
        this.includeSeparateEmployerContribution
      );

      return index === 0 ? planPremium : totalPremium.adding(planPremium, employee);
    }, null as unknown as Cost);

    // 2. Extract Conditional Logic for Multiple Plans
    const isMultiplePlans = plans.length > 1;
    const primaryPlan = plans[0];

    let whosCovered = '';
    let sumInsured = benefit.hideSumInsured ? Constants.EM_DASH : 'Multiple';
    let nomineeButtonText: string | undefined = undefined;
    let nomineeDescriptionHtml: string | undefined = undefined;

    if (!isMultiplePlans) {
      // Determine Who's Covered
      const selfCoverage = primaryPlan.familyDefinition?.includeSelf ? 'Self' : null;
      const dependentNames = selectedDependentsForPlan.map((d) => d.name);

      whosCovered = [
        selfCoverage,
        ...dependentNames,
      ]
        .filter((name): name is string => name !== null)
        .join(', ');

      // Determine Sum Insured
      if (!benefit.hideSumInsured) {
        sumInsured = primaryPlan.calculatedDescription(employee, selectedDependentsForPlan);
      }

      // Determine Nominee Details
      nomineeButtonText = this.getNomineeText(benefit, primaryPlan);

      const nominees = this.nomineeForBenefitPlan(primaryPlan);

      nomineeDescriptionHtml = nominees.length > 0 ?
        nominees.map((n) => `<p class="m-0"><span>${n.name} (${n.nomineeRelationship}) - ${n.nomineePercentage}%</span></p>`).join('<br>') :
        'Nominees are not yet added';
    }

    // 3. Return Clean ViewModel Literal
    return {
      id: `benefit-${benefit.id}`,
      benefitId: benefit.id,
      planId: primaryPlan.id,
      title: benefit.title,
      description: plans.map((plan) => plan.name).join(', '),
      type: BenefitsDisplayableType.BENEFIT,
      whosCovered,
      sumInsured,
      premium: premium ? premium.displayValue(employee, 0) : '',
      canDelete: !benefit.isMandatory,
      nomineeButtonText,
      nomineeDescriptionHtml,
      subViewModels: plans.map((plan) => {
        return this.planViewModel(plan, benefit, employee);
      }),
      dontAllowChangesAfterCheckout: false,
    };
  }

  planViewModel(plan: BenefitPlan,
    benefit: Benefit,
    employee : Employee) : CartSummaryTableRowViewModel {
    const selectedDependentsForPlan = this.dependentsForPlan(plan, employee);

    return {
      id: `plan-${plan.id}`,
      benefitId: benefit.id,
      planId: plan.id,
      title: plan.name,
      description: '',
      type: BenefitsDisplayableType.BENEFIT,
      whosCovered:
          [
            plan.familyDefinition?.includeSelf ? 'Self' : null,
            ...selectedDependentsForPlan.map((dependent) => dependent.name),
          ]
            .filter((d) => d !== null)
            .map((d) => d!)
            .join(', '),
      sumInsured: benefit.hideSumInsured ?
        Constants.EM_DASH :
        plan.calculatedDescription(
          employee,
          selectedDependentsForPlan
        ),
      premium: plan.getPremiumValueForPlan(benefit.mtPremiumCalculation,
        employee,
        benefit.policyType,
        selectedDependentsForPlan,
        this.includeSeparateEmployerContribution).displayValue(employee, 0),
      canDelete: !benefit.isMandatory,
      nomineeButtonText: this.getNomineeText(benefit, plan),
      nomineeDescriptionHtml: this.nomineeForBenefitPlan(plan)
        .map((nominee) => {
          return `<p class="m-0"><span>${nominee.name} (${nominee.nomineeRelationship}) - ${nominee.nomineePercentage}%</span></p>`;
        })
        .join('<br>') || 'Nominees are not yet added',
      subViewModels: [],
      dontAllowChangesAfterCheckout: benefit.dontAllowChangesAfterCheckout,
    };
  }

  // Retrieve nominee text
  getNomineeText(
    benefit: Benefit,
    plan: BenefitPlan
  ) {
    switch (benefit.requireNomineeConfiguration) {
        case RequireNominee.NOT_REQUIRED:
        case null:
        case undefined:
          return undefined;
        case RequireNominee.OPTIONAL:
        case RequireNominee.MANDATORY:
          switch (benefit.requireNomineeConfigurationForm) {
              case null:
              case undefined:
              case RequireNominee.NOT_REQUIRED:
                return (this.nomineeForBenefitPlan(plan)?.length ?? 0) > 0 ?
                  'Edit Nomination' :
                  'Complete your nomination';
              case RequireNominee.OPTIONAL:
                return this.nomineeUrlForBenefitPlan(plan.id) ? 'Edit Nomination' : 'Complete your nomination';
              case RequireNominee.MANDATORY:
                return this.nomineeUrlForBenefitPlan(plan.id) ? 'Edit Nomination' : 'Complete your nomination';
          }
    }
  }

  availableWalletAmount(
    employee: Employee,
    walletTransactions: WalletHistory[]
  ): WalletConsumption {
    if (walletTransactions.length === 0) {
      return {
        walletAvailableToConsume: new Cost(-1, CostType.FIXED, employee.wallet, 0, 0, 0, 0, 0, 0, 0, 0, 0, []),
        marketplaceConsumption: new Cost(-1, CostType.FIXED, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, []),
        lastEnrolmentConsumption: new Cost(-1, CostType.FIXED, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, []),
        lastBalanceWallet: new Cost(-1, CostType.FIXED, employee.wallet, 0, 0, 0, 0, 0, 0, 0, 0, 0, []),
      };
    }

    // ✅ Plan-related event types
    const planEventTypes = new Set([
      WalletEventType.ASSIGNMENT,
      WalletEventType.COMPLETE_CART,
      WalletEventType.MID_TERM,
      WalletEventType.ADJUSTED_COMPLETE_CART,
      WalletEventType.ADJUSTED_MID_TERM,
    ]);

    // ❌ Marketplace & Vital Plus event types (always frozen)
    const excludedEventTypes = new Set([
      WalletEventType.NEWMI_WELLNESS_OFFERING,
      WalletEventType.ADJUSTED_NEWMI_WELLNESS_OFFERING,
      WalletEventType.CLAIM_APPROVAL,
      WalletEventType.ADJUSTED_CLAIM_APPROVAL,
      WalletEventType.MARKETPLACE_WELLNESS_OFFERING,
    ]);

    // Last plan consumption (restore when revisiting plan page)
    const lastPlanConsumption = walletTransactions
      .filter((t) => planEventTypes.has(t.eventType) && !t.isDeleted)
      .at(-1)?.amount || 0;

    // Marketplace/Vital Plus consumption (permanently frozen)
    const marketplaceConsumption = walletTransactions
      .filter((t) => excludedEventTypes.has(t.eventType) && !t.isDeleted)
      .reduce((sum, t) => {
        const amount = t.amount ?? 0;

        if (t.eventType === WalletEventType.ADJUSTED_CLAIM_APPROVAL) {
          return sum - amount;
        }

        return sum + amount;
      }, 0);

    // Calculate wallet to add back: use lastPlanConsumption if it doesn't exceed available wallet after marketplace consumption, else cap it.
    const walletToAddBack = Math.max(0, Math.min(lastPlanConsumption, employee.wallet - marketplaceConsumption));

    const lastBalance = walletTransactions.at(-1)?.balanceWallet || 0;

    // ✅ Add back plan consumption, subtract marketplace/Vital Plus permanently
    const availableWallet = lastBalance + walletToAddBack;

    // ✅ Cap to employee’s wallet
    const walletAvailableToConsume = Math.min(availableWallet, employee.wallet);

    const lastEnrolmentConsumption = Math.min(lastPlanConsumption, employee.wallet);

    const marketPlaceDeduction = walletTransactions
      .filter((t) => [
        WalletEventType.NEWMI_WELLNESS_OFFERING,
        WalletEventType.ADJUSTED_NEWMI_WELLNESS_OFFERING,
        WalletEventType.MARKETPLACE_WELLNESS_OFFERING,
      ].includes(t.eventType))
      .filter((t) => !t.isDeleted)
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const claimsDeduction = walletTransactions
      .filter((t) => (
        t.eventType === WalletEventType.CLAIM_APPROVAL ||
        t.eventType === WalletEventType.ADJUSTED_CLAIM_APPROVAL
      ) && !t.isDeleted)
      .reduce((sum, t) => {
        const amount = t.amount ?? 0;

        if (t.eventType === WalletEventType.ADJUSTED_CLAIM_APPROVAL) {
          return sum - amount;
        }

        return sum + amount;
      }, 0);

    const otherDeduction = {
      marketplace: new Cost(-1, CostType.FIXED, marketPlaceDeduction, 0, 0, 0, 0, 0, 0, 0, 0, 0, []),
      claims: new Cost(-1, CostType.FIXED, claimsDeduction, 0, 0, 0, 0, 0, 0, 0, 0, 0, []),
    };

    return {
      walletAvailableToConsume: new Cost(-1, CostType.FIXED, walletAvailableToConsume, 0, 0, 0, 0, 0, 0, 0, 0, 0, []),
      marketplaceConsumption: new Cost(-1, CostType.FIXED, marketplaceConsumption, 0, 0, 0, 0, 0, 0, 0, 0, 0, []),
      lastEnrolmentConsumption: new Cost(-1, CostType.FIXED, lastEnrolmentConsumption, 0, 0, 0, 0, 0, 0, 0, 0, 0, []),
      lastBalanceWallet: new Cost(-1, CostType.FIXED, lastBalance, 0, 0, 0, 0, 0, 0, 0, 0, 0, []),
      otherDeduction,
    };
  }
}

export class CartSection {
  constructor(
    public benefitSectionId: number | null,
    public name: string,
    public benefitToBenefitOptionMap: Map<number, BenefitPlan[]>,
    public benefitsById: Map<number, Benefit>,
    public endorsementId?: number
  ) {}

  get hasAddedPlans() {
    return this.benefitToBenefitOptionMap.size > 0;
  }

  cost(benefitId: number, currentEmployee: Employee,
    useGrossCost: boolean = false) {
    const costs = this.benefitToBenefitOptionMap
      .get(benefitId)
      ?.filter((plan) => plan.isActive)
      ?.map((plan) => plan.netCost(currentEmployee, false, [], useGrossCost));

    return this.costSum(costs || []);
  }

  totalForSection(
    currentEmployee: Employee,
    dependentByPlanId: Map<number, number[]>,
    midTermCalculations: boolean,
    useGrossCost: boolean = false
  ) {
    const costs: Cost[] = [];

    this.benefitToBenefitOptionMap.forEach((plans, benefitId) => {
      costs.push(
        ...plans
          .map((plan) => (this.benefitsById.get(benefitId)?.mtPremiumCalculation === 1 ?
            plan.netCost(
              currentEmployee,
              midTermCalculations,
              (dependentByPlanId.get(plan.id) || [])
                .map((depId) => currentEmployee.dependents.find((d) => d.id === depId))
                .filter((d) => d)
                .map((d) => d!),
              useGrossCost
            ) :
            plan.netCostProRata(
              currentEmployee,
              this.benefitsById.get(benefitId)?.policyType ||
                PolicyGroupPolicyType.GMC,
              midTermCalculations,
              (dependentByPlanId.get(plan.id) || [])
                .map((depId) => currentEmployee.dependents.find((d) => d.id === depId))
                .filter((d) => d)
                .map((d) => d!),
              useGrossCost
            )))
      );
    });

    return this.costSum(costs);
  }

  private costSum(costs: Cost[]) {
    let sum = new Cost(
      Cost.NO_COST_ID,
      CostType.FIXED,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      []
    );

    costs.forEach((cost) => (sum = sum.addingValue(cost)));

    return sum;
  }

  get shouldShowNominee() {
    let show = false;

    this.benefitToBenefitOptionMap.forEach((_, benefitId) => {
      const benefit = this.benefitsById.get(benefitId);

      if (
        benefit &&
        benefit.requireNomineeConfiguration !== RequireNominee.NOT_REQUIRED
      ) {
        show = true;
      }
    });

    return show;
  }

  get showSumInsured() {
    const benefits: Benefit[] = [];

    this.benefitToBenefitOptionMap.forEach((plans, benefitId) => {
      if (plans.length > 0) {
        const b = this.benefitsById.get(benefitId);

        if (b) {
          benefits.push(b);
        }
      }
    });

    if (
      benefits.filter((benefit) => benefit.hideSumInsured).length ===
      benefits.length
    ) {
      return false;
    }

    let anyPlanHasSumInsured = false;

    this.benefitToBenefitOptionMap.forEach((plans) => {
      if (plans.length > 0) {
        anyPlanHasSumInsured =
          anyPlanHasSumInsured ||
          plans.filter((plan) => plan.sumInsured).length > 0;
      }
    });

    return anyPlanHasSumInsured;
  }

  sortedByBenefitOrdinal(
    a: KeyValue<number, BenefitPlan[]>,
    b: KeyValue<number, BenefitPlan[]>
  ) {
    void a;
    void b;

    return 1;
  }
}
